import React from 'react';
import type { ComparisonAverageScore } from '@app-types/comparison';
import './ComparisonAverageScoreTable.less';

export interface ComparisonAverageScoreTableProps {
  averageScore: ComparisonAverageScore;
}

interface HeaderCell {
  key: keyof ComparisonAverageScore;
  lines: readonly string[];
}

// Header labels come from Figma node 3197:10628; multi-line headers like
// `1ヶ月\n前` are rendered as two block-level `<span>` elements so screen
// readers announce the full label while CSS controls line breaks.
const HEADERS: readonly HeaderCell[] = [
  { key: 'current', lines: ['現在'] },
  { key: 'oneMonth', lines: ['1ヶ月', '前'] },
  { key: 'threeMonths', lines: ['3ヶ月', '前'] },
  { key: 'sixMonths', lines: ['6ヶ月', '前'] },
  { key: 'nineMonths', lines: ['9ヶ月', '前'] },
  { key: 'twelveMonths', lines: ['12ヶ月', '前'] },
];

/**
 * 6-column average score table used inside each ComparisonCard.
 * Renders a title (`平均スコア`) above a semantic `<table>` with a
 * visually-hidden caption for accessibility.
 *
 * Figma:
 * - Group (title + table): 3222:9871
 * - Table: 3197:10628 (82px total, 42px header row)
 */
const ComparisonAverageScoreTable: React.FC<ComparisonAverageScoreTableProps> = ({
  averageScore,
}) => (
  <div className="comparison-avg-score" data-node-id="3222:9871">
    <div className="comparison-avg-score__title">平均スコア</div>
    <table className="comparison-avg-score__table" data-node-id="3197:10628">
      <caption className="comparison-avg-score__caption">平均スコア</caption>
      <thead>
        <tr>
          {HEADERS.map((header) => (
            <th key={header.key} scope="col" className="comparison-avg-score__th">
              {header.lines.map((line) => (
                <span key={line} className="comparison-avg-score__th-line">
                  {line}
                </span>
              ))}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {HEADERS.map((header) => (
            <td key={header.key} className="comparison-avg-score__td">
              {averageScore[header.key]}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

export default ComparisonAverageScoreTable;
